import { z } from 'zod';
import { garmentTypeKeySchema } from '../constants/garment-types.js';
import { nonNegativePaiseSchema } from '../money/paise.js';
import {
  dateInputSchema,
  isoDateStringSchema,
  objectIdSchema,
  paginationQuerySchema,
  stateCodeSchema,
} from './common.js';
import {
  invoiceLineSchema,
  invoicePartySchema,
  invoiceRateSummaryRowSchema,
  invoiceTotalsSchema,
} from './invoices.js';
import { fabricInputSchema, fabricSchema } from './orders.js';

/**
 * The price, before anybody has committed to it.
 *
 * A quotation sits in the one gap this product had: an enquiry could be marked `quoted` without
 * anything recording WHAT was quoted, and an order went straight from booking to invoice. So a
 * shop could see that it had given somebody a price and not what the price was — which makes the
 * stage a flag rather than a fact, and makes "what did we say to them in July?" unanswerable.
 *
 * IT IS GIVEN TO A CUSTOMER, AND ONLY TO A CUSTOMER. The CRM has one contact record: somebody who
 * has only asked a price is a customer with no orders yet, so `customerId` is required when a
 * quotation is created and nothing else can own one. (The table's `lead_id` column is legacy — the
 * lead → customer data migration moved enquiries' quotations onto their customers — and no API
 * reads or writes it.)
 *
 * IT IS NOT A TAX DOCUMENT, and the distance matters more than the resemblance. It carries the
 * same tax arithmetic as an invoice — deliberately, so the figure quoted is the figure billed —
 * but it must never be titled TAX INVOICE, never draw from the invoice series, and never appear
 * in the sales register. Its own counter key enforces the second of those.
 *
 * IT HAS NO MEASUREMENTS. Measurements belong to a booking, not to a price: you do not take a
 * bridal customer's measurements before she has agreed what it costs. An accepted quotation
 * prefills the booking screen line for line, and the counter adds the measurements there.
 */

/* -------------------------------------------------------------------------------------------
 * Status and state
 * ---------------------------------------------------------------------------------------- */

/**
 * What is STORED on the document. Four values, each the result of somebody doing something.
 */
export const QUOTATION_STATUSES = ['draft', 'sent', 'accepted', 'rejected'] as const;
export type QuotationStatus = (typeof QUOTATION_STATUSES)[number];
export const quotationStatusSchema = z.enum(QUOTATION_STATUSES);

/**
 * What a SCREEN renders, which is the stored status plus two the passage of time decides.
 *
 * `expired` and `superseded` are computed on read and never written. Storing either would mean a
 * document whose state depends on a sweep having run — a quotation that is expired in fact and
 * `sent` in the database until some job catches up, which is the kind of disagreement a shop
 * notices and stops trusting the screen over.
 */
export const QUOTATION_STATES = [...QUOTATION_STATUSES, 'expired', 'superseded'] as const;
export type QuotationState = (typeof QUOTATION_STATES)[number];
export const quotationStateSchema = z.enum(QUOTATION_STATES);

export const QUOTATION_NOTES_MAX_LENGTH = 500;
export const QUOTATION_LINE_NOTES_MAX_LENGTH = 1000;
export const QUOTATION_REJECT_REASON_MAX_LENGTH = 500;
/** Past this a quotation is a catalogue, and the PDF stops being one page. */
export const MAX_QUOTATION_LINES = 50;

/* -------------------------------------------------------------------------------------------
 * Lines
 * ---------------------------------------------------------------------------------------- */

/**
 * One thing, priced. Usually a garment; sometimes a line off the price list.
 *
 * The shape is deliberately assignable to `PricedLine` in the API's `order-pricing.ts`, so the
 * quotation total is computed by the SAME `linePaiseOf` the order uses. Two implementations of
 * "what does this line cost" is how a quote and the order it becomes come to disagree.
 *
 * WHY A LINE NEED NOT BE A GARMENT. A quotation used to require a garment type on every line, which
 * was right while a quotation only ever priced stitching. It is wrong for a boutique: thread,
 * buttons, lining and zips are things a shop charges for, they are in the price list, and none of
 * them is a garment anybody measures. Requiring a garment type meant either leaving them off the
 * quote — so the total did not match the bill — or filing a reel of thread as clothing. So a line
 * now names a PRODUCT or a GARMENT TYPE, exactly one, the same either/or `dealLineSchema` uses.
 *
 * `garmentTypeLabel` STAYS REQUIRED THROUGHOUT, and it is the field that makes this work: it is the
 * frozen name the line prints under, resolved from the garment type or the product depending on
 * which kind it is. Every reader — the PDF, the card, the totals — goes on reading one field and
 * never asks which sort of line it has. (The name is now a little narrow for what it holds; it is
 * left alone here because renaming a field that is frozen onto every sent quotation in every shop
 * is a migration, and it buys nothing this change needs.)
 */
export const quotationLineSchema = z.object({
  id: objectIdSchema,
  lineNumber: z.number().int().positive(),
  /** The garment being stitched, or null on a product line. Exactly one of this and `productId`. */
  garmentTypeKey: z.string().nullable(),
  /** Which catalogue line this was priced off, or null on a garment line. Provenance only. */
  productId: objectIdSchema.nullable(),
  /**
   * What the line prints as — the garment type's label, or the product's name. Frozen at creation:
   * a garment type or a product the shop later renames must not rewrite a quote already sent.
   */
  garmentTypeLabel: z.string(),
  quantity: z.number().int().positive(),
  unitPricePaise: nonNegativePaiseSchema,
  /**
   * Who supplies the cloth, and what it costs when the shop does.
   *
   * Not optional decoration: in tailoring the cloth is routinely half the bill, and
   * `linePaiseOf` adds `metres × ratePerMetre` on top of the stitching charge. A quotation that
   * could not carry it would be quoting half the job.
   */
  fabric: fabricSchema.nullable(),
  /** Server-computed via `linePaiseOf`, so the client never has to reproduce the arithmetic. */
  linePaise: nonNegativePaiseSchema,
  notes: z.string().nullable(),
});
export type QuotationLine = z.infer<typeof quotationLineSchema>;

/**
 * Adding one. Says WHICH thing and what it costs; the server resolves and freezes the label.
 *
 * `superRefine` for the either/or, matching `dealLineInputSchema` and `createProfileSchema`: a
 * `z.union` reports every branch failing at once and reads as nonsense at the counter.
 *
 * Note that `unitPricePaise` is required for BOTH kinds here, unlike on a deal line where a product
 * may fall back to the catalogue. A quotation is a figure being handed to a customer, and there is
 * no reading of "leave the price out" that is safe to guess at on a document that gets sent.
 */
export const quotationLineInputSchema = z
  .object({
    garmentTypeKey: garmentTypeKeySchema.optional(),
    productId: objectIdSchema.optional(),
    quantity: z.number().int().positive().max(999).default(1),
    unitPricePaise: nonNegativePaiseSchema,
    fabric: fabricInputSchema.optional(),
    notes: z.string().trim().max(QUOTATION_LINE_NOTES_MAX_LENGTH).nullish(),
  })
  .superRefine((input, ctx) => {
    if (input.garmentTypeKey && input.productId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['garmentTypeKey'],
        message: 'A line prices a garment or a product, not both',
      });
    }
    if (!input.garmentTypeKey && !input.productId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['garmentTypeKey'],
        message: 'Say what this line is for',
      });
    }
  });
export type QuotationLineInput = z.input<typeof quotationLineInputSchema>;

/* -------------------------------------------------------------------------------------------
 * The document
 * ---------------------------------------------------------------------------------------- */

export const quotationSchema = z.object({
  id: objectIdSchema,

  /**
   * The customer it was given to. Every quotation created today has one; null only on a legacy
   * quotation given to an enquiry that never became a customer (the lead → customer migration
   * leaves those where they are).
   */
  customerId: objectIdSchema.nullable(),

  /**
   * Their name and number, FROZEN at creation and denormalised so the card and the PDF need no
   * second read.
   *
   * THE NAMES ARE A LITTLE NARROW FOR WHAT THEY HOLD — they carry the customer's details — and
   * they are left alone deliberately, for the reason `garmentTypeLabel`
   * is left alone further down this file: renaming a field that is frozen onto every sent quotation
   * in every shop is a migration, and it buys nothing this change needs. Everything that reads them
   * — the PDF, the search, the row, the card — reads a person's name and number and is correct
   * either way.
   */
  leadName: z.string(),
  leadPhone: z.string(),

  /**
   * The deal this was priced from, if it came off one.
   *
   * THE LINK LIVES ON THE QUOTATION rather than as a `quotationId` on the deal, because a deal can
   * produce several: a revision, or a second quote after the shop rethinks the job. A single slot
   * on the deal would be silently overwritten by the second one, and the first would lose the only
   * record of where it came from. Many quotations point at one deal; that is the true shape.
   *
   * NULL IS ORDINARY. The quotations screen creates quotations straight off a customer with no deal
   * anywhere in sight, and that path is untouched by any of this — it was the only path until now
   * and it stays a first-class one.
   *
   * Written once, at creation, and never editable afterwards: `updateQuotationSchema` does not
   * accept it, and `MUTABLE_AFTER_SEND` does not list it, so the send freeze covers it correctly.
   * Re-pointing a quotation at a different deal is not an edit anybody needs, and it would move a
   * figure onto a deal that never agreed it.
   */
  dealId: objectIdSchema.nullable(),

  /**
   * `QT/26-27/0007`, shared by every revision of this quotation. Null on a draft — the number is
   * drawn when it is sent, because a number taken by a draft that is discarded is a gap.
   */
  baseNumber: z.string().nullable(),
  /** The base number for version 1, `…-R2` and up for a revision. Null on a draft. */
  number: z.string().nullable(),
  version: z.number().int().positive(),

  status: quotationStatusSchema,
  /** What to render. See {@link QUOTATION_STATES} — never stored. */
  state: quotationStateSchema,

  financialYear: z.string().nullable(),
  /** Null means the price does not lapse. Printed on the document either way. */
  validUntil: isoDateStringSchema.nullable(),

  /** Frozen from shop settings at send, exactly as an invoice freezes them. */
  registered: z.boolean(),
  interState: z.boolean(),
  pricesIncludeTax: z.boolean(),
  placeOfSupplyStateCode: stateCodeSchema.nullable(),
  shop: invoicePartySchema.nullable(),

  /** What the shop typed. Present on a draft and unchanged by sending. */
  lines: z.array(quotationLineSchema),
  /**
   * The same lines after tax, split as the invoice splits them — stitching as a service at the
   * shop's SAC, shop-supplied cloth as goods at its HSN. Empty on a draft.
   */
  taxLines: z.array(invoiceLineSchema),
  rateSummary: z.array(invoiceRateSummaryRowSchema),
  totals: invoiceTotalsSchema.nullable(),

  /**
   * The pre-tax arithmetic, from `orderTotals`. Kept alongside the taxed totals because it is
   * what the booking screen reproduces from the prefilled prices — the taxed total is rounded
   * to the rupee and the booking is not, so the two can differ by up to 50 paise.
   */
  subtotalPaise: nonNegativePaiseSchema,
  discountPaise: nonNegativePaiseSchema,
  preTaxTotalPaise: nonNegativePaiseSchema,

  amountInWords: z.string().nullable(),
  notes: z.string().nullable(),

  /** The chain. A revision points back; the one it replaced points forward. */
  version1QuotationId: objectIdSchema.nullable(),
  revisionOfQuotationId: objectIdSchema.nullable(),
  supersededByQuotationId: objectIdSchema.nullable(),
  supersededAt: isoDateStringSchema.nullable(),

  sentAt: isoDateStringSchema.nullable(),
  acceptedAt: isoDateStringSchema.nullable(),
  rejectedAt: isoDateStringSchema.nullable(),
  rejectReason: z.string().nullable(),
  /** Set when the accepted quotation was actually booked, so the two can be read together. */
  convertedOrderId: objectIdSchema.nullable(),

  createdByUserId: z.string().nullable(),
  createdByName: z.string().nullable(),
  pdfGeneratedAt: isoDateStringSchema.nullable(),
  sentOnWhatsappAt: isoDateStringSchema.nullable(),

  createdAt: isoDateStringSchema,
  updatedAt: isoDateStringSchema,
});
export type Quotation = z.infer<typeof quotationSchema>;

/**
 * Every quotation the shop has out, across all its enquiries — what the quotations screen reads.
 *
 * The filter is by STORED status and not by the rendered state, and the difference is worth being
 * plain about: `expired` and `superseded` are computed on read, so no query can select them
 * without either storing what the calendar decides or re-deriving it after paging, and both of
 * those produce a count that disagrees with the list under it. A lapsed quotation therefore
 * appears under `sent`, wearing the badge that says it has lapsed. The badge is the honest place
 * for a fact the database does not hold.
 */
export const quotationSearchQuerySchema = paginationQuerySchema.extend({
  /** Free text: a quotation number, or the name of whoever it was given to. */
  q: z.string().trim().max(120).optional(),
  /** Repeat the parameter for several. Omitted means every quotation the shop has. */
  status: z
    .union([quotationStatusSchema, z.array(quotationStatusSchema)])
    .transform((value) => (Array.isArray(value) ? value : [value]))
    .optional(),
});
export type QuotationSearchQuery = z.infer<typeof quotationSearchQuerySchema>;

export const quotationPageSchema = z.object({
  items: z.array(quotationSchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  total: z.number().int(),
});
export type QuotationPage = z.infer<typeof quotationPageSchema>;

/** Every quotation given to one customer, newest first. What the customer's card reads. */
export const customerQuotationsSchema = z.object({
  customerId: objectIdSchema,
  customerName: z.string(),
  /** So the form knows whether to show tax at all before anything has been sent. */
  registered: z.boolean(),
  quotations: z.array(quotationSchema),
});
export type CustomerQuotations = z.infer<typeof customerQuotationsSchema>;

/* -------------------------------------------------------------------------------------------
 * Requests
 * ---------------------------------------------------------------------------------------- */

/**
 * The fields both write schemas share, before either adds its own rule.
 *
 * The update and revise schemas omit the owner: whose price this is is settled when the document is
 * opened, and an edit cannot change the answer.
 */
const quotationWriteFields = z.object({
  /** Whose price this is — the customer it is being given to. */
  customerId: objectIdSchema,
  lines: z.array(quotationLineInputSchema).min(1).max(MAX_QUOTATION_LINES),
  /** Document-level, as an order's is — `computeInvoiceTax` apportions it across the lines. */
  discountPaise: nonNegativePaiseSchema.default(0),
  /** Omitted means the service's default of fifteen days; explicit null means no expiry. */
  validUntil: dateInputSchema.nullish(),
  notes: z.string().trim().max(QUOTATION_NOTES_MAX_LENGTH).nullish(),
  placeOfSupplyStateCode: stateCodeSchema.optional(),
});

/**
 * Strict, so a body still naming a `leadId` is refused rather than quietly re-read: a quotation is
 * for a customer, and a lead is no longer somebody one can be for.
 */
export const createQuotationSchema = quotationWriteFields.strict();
export type CreateQuotationInput = z.input<typeof createQuotationSchema>;

/**
 * A draft only. Sending freezes the document; changing it afterwards means a revision.
 *
 * The owner is omitted: WHOSE this is is settled when the document is opened and is not a field
 * somebody corrects afterwards.
 */
export const updateQuotationSchema = quotationWriteFields
  .omit({ customerId: true })
  .partial()
  .refine((patch) => Object.keys(patch).length > 0, { message: 'Nothing to update' });
export type UpdateQuotationInput = z.input<typeof updateQuotationSchema>;

export const sendQuotationSchema = z.object({
  sentAt: dateInputSchema.optional(),
});
export type SendQuotationInput = z.input<typeof sendQuotationSchema>;

/**
 * Opens version n+1 as a draft, seeded from the version being revised.
 */
export const reviseQuotationSchema = quotationWriteFields.omit({ customerId: true }).partial();
export type ReviseQuotationInput = z.input<typeof reviseQuotationSchema>;

export const acceptQuotationSchema = z.object({
  acceptedAt: dateInputSchema.optional(),
});
export type AcceptQuotationInput = z.input<typeof acceptQuotationSchema>;

export const rejectQuotationSchema = z.object({
  reason: z.string().trim().max(QUOTATION_REJECT_REASON_MAX_LENGTH).nullish(),
});
export type RejectQuotationInput = z.input<typeof rejectQuotationSchema>;

export const sendQuotationOnWhatsappSchema = z.object({
  /** Defaults to the number the quotation was given to. */
  to: z.string().trim().optional(),
  caption: z.string().trim().max(400).optional(),
});
export type SendQuotationOnWhatsappInput = z.input<typeof sendQuotationOnWhatsappSchema>;

/** A signed link to the rendered PDF. A4 only — a quotation has no thermal variant. */
export const quotationDownloadSchema = z.object({
  url: z.string(),
  expiresAt: isoDateStringSchema,
  filename: z.string(),
});
export type QuotationDownload = z.infer<typeof quotationDownloadSchema>;
